import React, { useCallback, useRef, useState } from 'react';
import { Upload } from 'lucide-react';

const InteriorCanvas: React.FC = () => {
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFile = (file: File) => {
        if (!file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (e) => setUploadedImage(e.target?.result as string);
        reader.readAsDataURL(file);
    };

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    }, []);

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDragLeave = () => setIsDragging(false);

    return (
        <div className="flex-1 flex flex-col items-center justify-center p-6 relative">
            {!uploadedImage ? (
                <div
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    className={`w-full max-w-2xl aspect-video rounded-2xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center gap-4 ${isDragging
                        ? 'border-violet-500 bg-violet-500/10'
                        : 'border-zinc-700 bg-zinc-900/30 hover:border-zinc-500 hover:bg-zinc-900/50'
                        }`}
                >
                    <div className="w-16 h-16 rounded-2xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                        <Upload size={28} className="text-violet-400" strokeWidth={1.5} />
                    </div>
                    <div className="text-center">
                        <p className="text-sm font-semibold text-zinc-300">Upload Interior Photo</p>
                        <p className="text-xs text-zinc-600 mt-1">Drag & drop or click to browse</p>
                        <p className="text-[10px] text-zinc-700 mt-2">JPG, PNG, WEBP supported</p>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                    />
                </div>
            ) : (
                <div className="w-full max-w-2xl flex flex-col gap-4">
                    <div className="relative rounded-2xl overflow-hidden border border-zinc-800 shadow-2xl">
                        <img
                            src={uploadedImage}
                            alt="Uploaded interior"
                            className="w-full object-contain max-h-[60vh]"
                        />
                        <button
                            onClick={() => setUploadedImage(null)}
                            className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm border border-white/10 text-zinc-400 hover:text-white px-3 py-1.5 rounded-lg text-xs transition-all"
                        >
                            Replace
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InteriorCanvas;
